import React, { useEffect, useMemo, useRef, useState } from "react";
import { IonInput, IonItem } from "@ionic/react";

const ROUNDING_FACTOR_BASE = 10;
const MAX_FIXED_DECIMALS = 20;

type Props = {
    value?: number;
    onValueChange: (value: number) => void;
    placeholder?: string;
    locale?: string;
    currency?: string;
    decimals?: number;
    allowNegative?: boolean;
    rightAlign?: boolean;
};

const CurrencyInput: React.FC<Props> = ({
                                            value = 0,
                                            onValueChange,
                                            placeholder = "Enter amount",
                                            locale,
                                            currency,
                                            decimals = 2,
                                            allowNegative = false,
                                            rightAlign = true,
                                        }) => {
    const effectiveLocale = useMemo(() => locale || navigator.language || "en-US", [locale]);
    const formatter = useMemo(
        () =>
            new Intl.NumberFormat(effectiveLocale, {
                style: currency ? "currency" : "decimal",
                currency: currency || undefined,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }),
        [effectiveLocale, currency, decimals]
    );

    const [display, setDisplay] = useState<string>(() =>
        Number.isFinite(value) ? formatter.format(value) : ""
    );
    const focusedRef = useRef(false);

    useEffect(() => {
        if (!focusedRef.current) {
            setDisplay(Number.isFinite(value) ? formatter.format(value) : "");
        }
    }, [value, formatter]);


    const parseFlexibleNumber = (raw: string): number => {
        if (!raw) return NaN;

        let s = raw.replace(/[^\d,.\-]/g, "");

        let neg = false;
        if (allowNegative && s.startsWith("-")) {
            neg = true;
            s = s.slice(1).replace(/-/g, "");
        } else {
            s = s.replace(/-/g, "");
        }

        const lastComma = s.lastIndexOf(",");
        const lastDot = s.lastIndexOf(".");
        let decSep: "," | "." | null = null;

        if (lastComma === -1 && lastDot === -1) {
            decSep = null;
        } else if (lastComma === -1) {
            decSep = ".";
        } else if (lastDot === -1) {
            decSep = ",";
        } else {
            decSep = lastComma > lastDot ? "," : ".";
        }

        let normalized: string;

        if (decSep) {
            const idx = s.lastIndexOf(decSep);
            const intPart = s.slice(0, idx).replace(/[.,]/g, "");
            const fracPart = s.slice(idx + 1).replace(/[.,]/g, "");
            normalized = `${neg ? "-" : ""}${intPart || "0"}.${fracPart}`;
        } else {
            normalized = `${neg ? "-" : ""}${s.replace(/[.,]/g, "")}`;
        }

        if (normalized === "-" || normalized === "" || normalized === "." || normalized === "-.") {
            return NaN;
        }

        const n = parseFloat(normalized);
        return Number.isFinite(n) ? n : NaN;
    };

    const formatForDisplay = (n: number): string => {
        if (!Number.isFinite(n)) return "";
        const factor = Math.pow(ROUNDING_FACTOR_BASE, decimals);
        const rounded = Math.round(n * factor) / factor;
        return formatter.format(rounded);
    };

    const handleInput = (e: any) => {
        const raw: string = e?.detail?.value ?? "";
        setDisplay(raw);

        const parsed = parseFlexibleNumber(raw);
        onValueChange(parsed);
    };

    const handleFocus = () => {
        focusedRef.current = true;
        const n = Number.isFinite(value) ? value : parseFlexibleNumber(display);
        if (Number.isFinite(n)) {
            const s = n.toFixed(Math.min(decimals, MAX_FIXED_DECIMALS)).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
            setDisplay(s);
        }
    };

    const handleBlur = () => {
        focusedRef.current = false;
        const parsed = parseFlexibleNumber(display);
        setDisplay(formatForDisplay(parsed));
        onValueChange(parsed);
    };

    return (
        <IonItem className={`currency-input-item ${rightAlign ? "currency-input--right" : ""}`}>
            <IonInput
                type="text"
                inputmode="decimal"
                value={display}
                placeholder={placeholder}
                onIonInput={handleInput}
                onIonFocus={handleFocus}
                onIonBlur={handleBlur}
                className="currency-input"
                color="dark"
            />
        </IonItem>
    );
};

export default CurrencyInput;
